import { useUser } from '~/modules/auth/hooks/useUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import type { ResourceType } from '~settings/api/lib/schemas';
import {
  type UserListItem,
  type UserType,
  UserTypeSchema,
} from '~settings/api/lib/schemas';

const USER_TYPE_ORDER = {
  [UserTypeSchema.enum.INTERNAL]: -1,
  [UserTypeSchema.enum.EXTERNAL]: 0,
  [UserTypeSchema.enum.VIEWER]: 1,
  [UserTypeSchema.enum.MANAGER]: 2,
  [UserTypeSchema.enum.ADMIN]: 3,
  [UserTypeSchema.enum.SUPERADMIN]: 4,
} as const;

function isHigherUserType(userType?: UserType, otherUserType?: UserType) {
  if (userType === otherUserType) {
    return true;
  }

  if (!userType || !otherUserType) {
    return false;
  }

  return USER_TYPE_ORDER[userType] >= USER_TYPE_ORDER[otherUserType];
}

export function useCanEditUser(
  user: Pick<UserListItem, 'roleResources' | 'userType' | 'isInternal'>,
) {
  const [manageExternalUser, internalUserUpdates] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsManageExternalUser,
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);
  const { hasPermission, userType } = useUser();

  if (!manageExternalUser) {
    return false;
  }

  if (user.isInternal && !internalUserUpdates) {
    return false;
  }

  if (!isHigherUserType(userType, user.userType)) {
    return false;
  }

  const seen = new Set<string>();
  const labels: { id: string; type: ResourceType }[] = [];

  for (const [, resources] of Object.entries(user.roleResources)) {
    for (const resource of resources) {
      if (resource.type === 'LABEL' && !seen.has(resource.id)) {
        seen.add(resource.id);
        labels.push({ id: resource.id, type: 'LABEL' });
      }
    }
  }

  return hasPermission('USER.EDIT', [
    { id: 'app:ADA', type: 'APPLICATION' },
    ...labels,
  ]);
}
